import { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  DetailItem,
  DetailsGrid,
  DetailsSection,
  SummarySection,
} from "./styles";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";

import {
  TInvoiceEntryStatus,
  setIsLoadingIssuedInvoices,
  setIsRestoringQuerystring,
} from "../../../../redux/reducers/globalState";
import { useTypedDispatch } from "../../../../hooks/useTypedDispatch";
import {
  fetchExportedIssuedInvoice,
  retryIssuedInvoiceExport,
} from "../../../../redux/actions";
import { setToastState } from "../../../../redux/reducers/globalState";
import { useNavigationManager } from "../../../../hooks/useNavigationManager";
import { sub } from "date-fns/sub";
import { format } from "date-fns/format";

import { ROUTES } from "../../../../router";
import axios, { CancelTokenSource } from "axios";
import Button from "../../../../components/ui-v2/Button";

import { TIssuedExportedInvoice, TIssuedInvoiceExport } from "./mock";
import Tag from "../../../../components/ui-v2/Tag";
import BreadCrumbs from "../../../../components/ui-v2/BreadCrumbs";

import { TStatus } from "../../mock";
import { useParams } from "react-router-dom";
import { parseISO } from "date-fns/parseISO";
import Skeleton from "../../../../components/ui-v2/Skeleton";
import { TExportStatus, TNature } from "../mock";
import History, { THistoryItem } from "../../../../components/ui-v2/History";

function IssuedInvoicesExportDetails() {
  const dispatch = useTypedDispatch();
  const { goTo } = useNavigationManager();

  const [_shouldOpenExportIssuedModal, setShouldOpenExportIssuedModal] =
    useState(false);

  const payableModalInvoiceRef = useRef<TIssuedExportedInvoice | undefined>();

  const fetchInvoicesCancelTokenRef = useRef<CancelTokenSource>(
    axios.CancelToken.source()
  );

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const { exportedIssuedInvoice } = useTypedSelector(
    (state) => state.globalState.data
  );

  const isRestoringQuerystring = useTypedSelector(
    (state) => state.globalState.isRestoringQuerystring
  );

  const reduxQuerystring = useTypedSelector((state) => state.querystring);

  const startDateRef = useRef<string | undefined>(
    format(sub(new Date(), { days: 30 }), "yyyy-MM-dd")
  );
  const endDateRef = useRef<string | undefined>(
    format(new Date(), "yyyy-MM-dd")
  );

  const entryStatusRef = useRef<TInvoiceEntryStatus | undefined>(
    reduxQuerystring.launch_status === 40
      ? undefined
      : (reduxQuerystring.launch_status as TInvoiceEntryStatus)
  );

  const payablesRef = useRef<boolean | undefined>();
  const goodsEntryRef = useRef<boolean | undefined>();
  const pageRef = useRef(1);
  const itemsPerPageRef = useRef(100);

  const allInvoicesRef = useRef<TIssuedInvoiceExport | null>(null);

  const { exportId } = useParams();

  const updateAllInvoicesRef = useCallback(
    (_entryStatus?: number, _endDate?: string) => {
      const queryStringParams = { ...reduxQuerystring };
      if (!queryStringParams.modal) {
        endDateRef.current = queryStringParams.end_date;
        startDateRef.current = queryStringParams.start_date;
        payablesRef.current = queryStringParams.has_payable_accounts;
        goodsEntryRef.current = queryStringParams.has_goods_receipt;
        pageRef.current = queryStringParams.page;
        itemsPerPageRef.current = queryStringParams.items_per_page;
        entryStatusRef.current = queryStringParams.launch_status as
          | TInvoiceEntryStatus
          | undefined;

        // Cancel previous request
        if (fetchInvoicesCancelTokenRef.current) {
          fetchInvoicesCancelTokenRef.current.cancel(
            "Operation canceled due to new request."
          );
        }
        // Create new token for this request
        fetchInvoicesCancelTokenRef.current = axios.CancelToken.source();

        dispatch(
          fetchExportedIssuedInvoice({
            exportId: exportId as string,
            cancelToken: (
              fetchInvoicesCancelTokenRef.current as CancelTokenSource
            ).token,
          })
        )
          .unwrap()
          .then((response) => {
            allInvoicesRef.current = response;
          })
          .catch((e) => {
            if (axios.isCancel(e)) {
              // Request was canceled, do nothing
            } else {
              console.log(e);
            }
          });
      }
    },
    [window.location.search, reduxQuerystring]
  );

  const debouncedUpdateAllInvoicesRef = useCallback(
    (_entryStatus?: number | undefined, _endDate?: string | undefined) => {
      const queryStringParams = { ...reduxQuerystring };
      if (!queryStringParams.modal && !isRestoringQuerystring) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        dispatch(setIsLoadingIssuedInvoices(true));
        debounceRef.current = setTimeout(() => {
          updateAllInvoicesRef(_entryStatus, _endDate);
        }, 500);
      }

      if (isRestoringQuerystring) {
        dispatch(setIsRestoringQuerystring(false));
      }
    },
    [updateAllInvoicesRef]
  );

  const handlePickExportStatus = (status: TExportStatus) => {
    switch (status) {
      case "Finalizada":
        return <Tag color="green" icon="noIcon" size="medium" text={status} />;
      case "Erro ao processar":
        return <Tag color="red" icon="noIcon" size="medium" text={status} />;
      case "Em processamento":
        return <Tag color="orange" icon="noIcon" size="medium" text={status} />;
    }
  };

  const handlePickStatus = (status: TStatus) => {
    switch (status) {
      case "Autorizada em conting\u00EAncia":
      case "Autorizada":
        return (
          <Tag
            color="green"
            icon="checkCircle"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Cancelada":
        return (
          <Tag
            color="darkGray"
            icon="timesCircle"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Em processamento":
        return (
          <Tag
            color="blue"
            icon="clock"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Rejeitada":
      case "Denegada":
        return (
          <Tag
            color="red"
            icon="exclamationTriangle"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Inutilizada":
        return (
          <Tag
            color="orange"
            icon="trash"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      default:
        return <Tag color="blue" icon="noIcon" size="medium" text={"Todos"} />;
    }
  };

  const handlePickNature = (nature: TNature) => {
    switch (nature) {
      case "001 - Compra":
      case "002 - Venda":
      case "003 - Transferência":
      case "004 - Devolução":
      case "005 - Importação":
      case "006 - Exportação":
      case "007 - Remessa":
      case "008 - Retorno":
      case "009 - Outras entradas":
      case "010 - Outras saídas":
      case "011 - Serviços - ISSQN":
      case "012 - Lançamento de Crédito":
      case "013 - Recebimento":
      case "014 - Ressarcimento":
      case "015 - Serviço - ICMS":
        return <Tag color="blue" icon="noIcon" size="medium" text={nature} />;
      default:
        return <Tag color="blue" icon="noIcon" size="medium" text={"Todas"} />;
    }
  };

  const handlePickHistoryItems = (
    historyItem: TIssuedInvoiceExport["history"][0]
  ): THistoryItem => {
    switch (historyItem.status) {
      case "exporta\u00E7\u00E3o criada":
        return {
          title: historyItem.status,
          author: historyItem.created_by,
          date: format(
            parseISO(historyItem.created_at),
            "dd/MM/yyyy 'às' HH:mm"
          ),
          color: "blue",
        };
      case "em processamento":
        return {
          title: historyItem.status,
          author: historyItem.created_by,
          date: format(
            parseISO(historyItem.created_at),
            "dd/MM/yyyy 'às' HH:mm"
          ),
          color: "orange",
        };
      case "erro ao processar":
        return {
          title: historyItem.status,
          author: historyItem.created_by,
          date: format(
            parseISO(historyItem.created_at),
            "dd/MM/yyyy 'às' HH:mm"
          ),
          color: "red",
        };
      case "processamento completo":
      case "exportação finalizada":
        return {
          title: historyItem.status,
          author: historyItem.created_by,
          date: format(
            parseISO(historyItem.created_at),
            "dd/MM/yyyy 'às' HH:mm"
          ),
          color: "green",
        };
    }
  };

  useEffect(() => {
    debouncedUpdateAllInvoicesRef();
  }, [debouncedUpdateAllInvoicesRef]);

  useEffect(() => {
    if (window.location.pathname === "/") {
      goTo(ROUTES.INVOICES, reduxQuerystring);
    }
  }, [window.location.pathname]);

  return (
    <Container>
      <SummarySection>
        <div>
          <BreadCrumbs
            names={["Notas emitidas", "Exportações", "Detalhes"]}
            urls={[
              ROUTES.INVOICES_ISSUED,
              ROUTES.EXPORT_ISSUED,
              ROUTES.EXPORT_ISSUED_DETAILS,
            ]}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
              marginTop: 12,
            }}
          >
            <h1>Exportação #{exportId}</h1>
            {exportedIssuedInvoice &&
              handlePickExportStatus(exportedIssuedInvoice?.export_status)}
          </div>
        </div>
        <div className="buttons-container">
          {exportedIssuedInvoice &&
            exportedIssuedInvoice.export_status === "Erro ao processar" && (
              <Button
                icon="sync"
                iconPosition="left"
                variant="secondary"
                text="Tentar novamente"
                style={{ padding: "12px 16px" }}
                onClick={() => {
                  if (!exportedIssuedInvoice?.uuid) return;
                  const token = axios.CancelToken.source();
                  dispatch(
                    retryIssuedInvoiceExport({
                      exportUuid: exportedIssuedInvoice.uuid,
                      cancelToken: token.token,
                    })
                  )
                    .unwrap()
                    .then(() => {
                      dispatch(
                        setToastState({
                          variant: "success",
                          shouldShow: true,
                          message: {
                            title: "Reprocessando",
                            description:
                              "A exportação foi reenviada para processamento",
                          },
                        })
                      );
                      // Recarrega o detalhe para refletir o novo status/histórico.
                      debouncedUpdateAllInvoicesRef();
                    })
                    .catch(() => {
                      dispatch(
                        setToastState({
                          variant: "error",
                          shouldShow: true,
                          message: {
                            title: "Erro",
                            description:
                              "Não foi possível reprocessar a exportação",
                          },
                        })
                      );
                    });
                }}
              />
            )}
          <div></div>
          <Button
            icon="plus"
            iconPosition="left"
            variant="primary"
            style={{ padding: "12px 16px" }}
            text="Exportar XMLs"
            disabled={
              exportedIssuedInvoice
                ? exportedIssuedInvoice.export_status !== "Finalizada"
                : true
            }
            onClick={() => {
              payableModalInvoiceRef.current = undefined;
              setShouldOpenExportIssuedModal(true);
            }}
          />
        </div>
      </SummarySection>
      <DetailsSection>
        <DetailsGrid>
          <DetailItem>
            <h3>Período</h3>
            {exportedIssuedInvoice !== null ? (
              <span className="value">{`${format(
                parseISO(exportedIssuedInvoice?.start_date ?? ""),
                "dd/MM/yyyy"
              )} - ${format(
                parseISO(exportedIssuedInvoice?.end_date ?? ""),
                "dd/MM/yyyy"
              )}`}</span>
            ) : (
              <Skeleton variant="box" width={200} height={20} />
            )}
          </DetailItem>
          <DetailItem>
            <h3>Criado em</h3>
            {exportedIssuedInvoice !== null ? (
              <span className="value">
                {format(
                  parseISO(exportedIssuedInvoice?.created_at ?? ""),
                  "dd/MM/yyyy 'às' HH:mm"
                )}{" "}
                por {exportedIssuedInvoice?.created_by}
              </span>
            ) : (
              <Skeleton variant="box" width={200} height={20} />
            )}
          </DetailItem>
          <DetailItem>
            <h3>Status</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {exportedIssuedInvoice !== null ? (
                exportedIssuedInvoice.status.map((status) => (
                  <div key={status} style={{ marginBottom: 8 }}>
                    {handlePickStatus(status)}
                  </div>
                ))
              ) : (
                <Skeleton variant="box" width={200} height={20} />
              )}
            </div>
          </DetailItem>
          <DetailItem>
            <h3>Natureza da operação</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {exportedIssuedInvoice !== null ? (
                exportedIssuedInvoice.nature.map((nature) => (
                  <div key={nature} style={{ marginBottom: 8 }}>
                    {handlePickNature(nature)}
                  </div>
                ))
              ) : (
                <Skeleton variant="box" width={200} height={20} />
              )}
            </div>
          </DetailItem>
        </DetailsGrid>
        {exportedIssuedInvoice !== null ? (
          <History
            title="Histórico"
            items={exportedIssuedInvoice.history.map(handlePickHistoryItems)}
          />
        ) : (
          <Skeleton variant="box" width={320} height={200} />
        )}
      </DetailsSection>
    </Container>
  );
}

export default IssuedInvoicesExportDetails;
